import { useQuery } from "@tanstack/react-query"
import { DateTime } from "luxon"

import { type ChartConfig } from "@/components/ui/chart.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"

import { getRollingKPIData } from "../api/getRollingKPIData.ts"

import RollingChartDisplay from "./RollingChartDisplay.tsx"

import type { RollingChartConfig, RollingChartData } from "../types/rollingChartTypes.ts"

export default function RollingChart({ config }: { config: RollingChartConfig }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["rollingChart", { config }],
    queryFn: () => getRollingKPIData(config),
  })

  const chartConfig = {
    primary1: {
      label: data?.at(0)?.name || "",
      color: "var(--chart-1)",
    },
    primary2: {
      label: data?.at(1)?.name || "",
      color: "var(--chart-3)",
    },
    secondary: {
      label: config.primaryKPIs.length == 2 ? data?.at(2)?.name || "" : data?.at(1)?.name || "",
      color: "var(--chart-2)",
    },
  } satisfies ChartConfig

  const chartData: RollingChartData[] = data
    ? data[0].values?.map((v, i) => ({
        date: DateTime.fromISO(v.date).toFormat("L/d"),
        primary1: v.value,
        primary2: config.primaryKPIs.length == 2 ? data.at(1)?.values?.at(i)?.value : undefined,
        secondary:
          config.primaryKPIs.length == 2
            ? data.at(2)?.values?.at(i)?.value
            : data.at(1)?.values?.at(i)?.value,
      })) || []
    : []

  return (
    <div className="rounded-md shadow-sm border h-full p-4 flex flex-col bg-card">
      <h2 className="text-lg font-semibold mb-1">{config.label || "Rolling Gas Trend"}</h2>
      <Label className="text-muted-foreground text-[12pt]">{config.averageDays} Day Average</Label>
      {isPending && <Skeleton className="w-full grow" />}
      {isError && (
        <div className="text-status-bad min-h-[425px] w-full mt-[10px] font-semibold">
          Network Error
        </div>
      )}
      {data && (
        <RollingChartDisplay
          settings={config}
          config={chartConfig}
          data={chartData}
          showSecondary={config.secondaryKPI != undefined}
          primaryLabel={data[0].unit}
          secondaryLabel={config.primaryKPIs.length == 2 ? data.at(2)?.unit : data.at(1)?.unit}
        />
      )}
    </div>
  )
}
